import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Eye, Loader2, PenSquare, Search } from 'lucide-react';
import Modal from '../../../components/ui/Modal';
import { ApiService } from '../../../services/api';
import type { PracticeExam } from '../../../types/practice-exam';

const PAGE_SIZE = 50;

interface SchoolFacet {
    id: string;
    name: string;
    count: number;
}

interface ExamResultsTableProps {
    exam: PracticeExam;
    canViewSensitiveInfo: boolean;
    // Hesabı olmayan (kurum dışı / eşleşmeyen) satırın T.C., okul no, ad ve sınıfını düzeltme
    canEditIdentity: boolean;
    onOpenDetail: (row: any) => void;
    onMetadata: (metadata: { bookletMapping?: any; testNames?: any }) => void;
}

const rankLabel = (rank?: { rank: number; participantCount: number } | null) => (rank ? `${rank.rank}/${rank.participantCount}` : '-');

/**
 * Deneme sonuçları: okul süzgeci, arama, sayfalama (kurum dışı LGS'de binlerce satır olabilir; süzme sunucuda yapılır)
 * ve kimliği eksik satırların düzeltilmesi.
 */
export default function ExamResultsTable({ exam, canViewSensitiveInfo, canEditIdentity, onOpenDetail, onMetadata }: ExamResultsTableProps) {
    const [page, setPage] = useState(1);
    const [schoolId, setSchoolId] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [missingOnly, setMissingOnly] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<any[]>([]);
    const [count, setCount] = useState(0);
    const [schools, setSchools] = useState<SchoolFacet[]>([]);
    const [identityRow, setIdentityRow] = useState<any>(null);

    // Yazarken her harfte istek atılmasın
    useEffect(() => {
        const timer = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 350);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        ApiService.getPracticeExamResults(exam.id, { page, limit: PAGE_SIZE, schoolId, search, missingIdentity: missingOnly })
            .then((res: any) => {
                if (cancelled) return;
                setRows(Array.isArray(res?.rows) ? res.rows : []);
                setCount(Number(res?.count) || 0);
                if (Array.isArray(res?.schools)) setSchools(res.schools);
                onMetadata({ bookletMapping: res?.bookletMapping || {}, testNames: res?.testNames || {} });
            })
            .catch((err: any) => { if (!cancelled) setError(err?.message || 'Sonuçlar alınırken bir hata oluştu.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // onMetadata bağımlılıklarda yok: üst bileşenin durum ayarlayıcısı, yeniden yükleme sebebi değil
    }, [exam.id, page, schoolId, search, missingOnly, reloadKey]);

    const pageCount = Math.max(1, Math.ceil(count / PAGE_SIZE));
    const hasExternal = schools.some((school) => school.id !== 'internal');
    const isLgs = exam.levelType === 'lgs';
    const isAlanPuani = Boolean(exam.alanPuani) && !isLgs;
    // Akademi YKS denemesi: TYT puanı ve YKS (alan) puanı ayrı sütunda; sunucu tam YKS'de satıra alanPuani (yontem 'yks') ekler
    const isYks = exam.levelType === 'yks' && (exam.systemType === 'internal' || Boolean((exam as any).isInternal)) && !isAlanPuani;
    const filtered = Boolean(schoolId || search || missingOnly);
    const allCount = schools.reduce((sum, school) => sum + school.count, 0);

    return (
        <div className="space-y-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                {hasExternal && (
                    <select
                        value={schoolId}
                        onChange={(e) => { setSchoolId(e.target.value); setPage(1); }}
                        className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm sm:max-w-xs">
                        <option value="">Bütün okullar ({allCount})</option>
                        {schools.map((school) => (
                            <option key={school.id} value={school.id}>{school.name} ({school.count})</option>
                        ))}
                    </select>
                )}
                <label className="relative flex-1 sm:max-w-xs">
                    <Search size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Ad ya da okul no ara"
                        className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-2 text-sm"
                    />
                </label>
                {canEditIdentity && (
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={missingOnly} onChange={(e) => { setMissingOnly(e.target.checked); setPage(1); }} />
                        Yalnız kimliği eksik olanlar
                    </label>
                )}
                <span className="text-sm text-gray-500 sm:ml-auto">{loading ? 'Yükleniyor…' : `${count} öğrenci`}</span>
            </div>

            {error && !loading && <p className="text-sm text-red-600">{error}</p>}
            {!error && !loading && rows.length === 0 && (
                <p className="text-sm text-gray-500">{filtered ? 'Süzgece uyan sonuç yok.' : 'Bu deneme için sonuç bulunamadı.'}</p>
            )}

            {rows.length > 0 && (
                <div className={`max-h-[60vh] overflow-auto rounded-lg border border-gray-200 ${loading ? 'opacity-60' : ''}`}>
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                        <thead className="sticky top-0 bg-gray-50">
                            <tr>
                                <th className="px-3 py-2 text-left font-semibold text-gray-700">Öğrenci</th>
                                {canViewSensitiveInfo && <th className="px-3 py-2 text-left font-semibold text-gray-700">TC</th>}
                                <th className="px-3 py-2 text-left font-semibold text-gray-700">Okul No</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-700">Sınıf</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-700">Cinsiyet</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-700">Kitapçık</th>
                                {canViewSensitiveInfo && <th className="px-3 py-2 text-left font-semibold text-gray-700">Tel No</th>}
                                {isYks && <th className="px-3 py-2 text-right font-semibold text-gray-700">TYT Puanı</th>}
                                <th className="px-3 py-2 text-right font-semibold text-gray-700">{isLgs ? 'Net / Sıra' : isAlanPuani ? 'Alan Puanı / Sıra' : isYks ? 'YKS Puanı / Alan Sırası' : 'Genel Başarı'}</th>
                                <th className="px-3 py-2 text-center font-semibold text-gray-700">Detay</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                            {rows.map((row: any) => {
                                const stats = row?.stats || {};
                                const summary = row?.results?.summary || {};
                                const studentInfo = row?.results?.studentInfo || {};
                                const meta = row?.student?.metadata || {};

                                const tc = canViewSensitiveInfo ? (meta.tcNumber || studentInfo?.tc || '-') : '';
                                const firstName = row?.student?.firstName || '';
                                const lastName = row?.student?.lastName || '';
                                const parsedName = studentInfo?.fullName || meta.parsedName || '';
                                const fullName = (firstName || lastName) ? `${firstName} ${lastName}`.trim() : (parsedName || '-');

                                const externalSchool = row?.student?.external?.schoolName || '';
                                const schoolCode = row?.student?.studentNumber || meta.studentNumber || row?.schoolCode || studentInfo?.schoolCode || '-';
                                const sinif = row?.student?.department?.name || meta.className || '-';
                                const rawGender = meta.gender || studentInfo?.gender || '';
                                const cinsiyet = rawGender === 'K' ? 'Kadın' : rawGender === 'E' ? 'Erkek' : rawGender || '-';
                                const kitapcik = row?.booklet || studentInfo?.bookletType || summary?.bookletType || '-';
                                const telNo = canViewSensitiveInfo ? (studentInfo?.phone || row?.student?.phone || meta.phone || '-') : '';
                                const scorePercentage = stats.scorePercentage ?? stats.accuracyRate ?? summary.accuracyRate ?? 0;
                                const lgs = row?.results?.lgsScoring;
                                const alan = isAlanPuani ? row?.results?.alanPuani : null;
                                const yks = isYks ? row?.results?.alanPuani : null;
                                // Sunucunun alan bilgisi varsa TYT puanı ondan: TYT'si olmayana taban puan (100) yazılmasın
                                const tytPuani = yks ? yks.tytScore : (stats?.scoring?.sessionScores?.tytScore ?? null);

                                const withoutAccount = !row?.student?.id;
                                const missing = withoutAccount ? [!meta.tcNumber && 'T.C.', !meta.studentNumber && 'okul no'].filter(Boolean) : [];

                                return (
                                    <tr key={row.id} className="hover:bg-gray-50">
                                        <td className="whitespace-nowrap px-3 py-2 text-gray-800">
                                            {fullName}
                                            {externalSchool && <span className="block text-xs text-amber-700">{externalSchool}</span>}
                                            {missing.length > 0 && row?.student?.external && (
                                                <span className="block text-xs text-red-600">{missing.join(' ve ')} eksik: karnesine ulaşamaz</span>
                                            )}
                                        </td>
                                        {canViewSensitiveInfo && <td className="whitespace-nowrap px-3 py-2 text-gray-800">{tc}</td>}
                                        <td className="whitespace-nowrap px-3 py-2 text-gray-800">{schoolCode}</td>
                                        <td className="whitespace-nowrap px-3 py-2 text-gray-800">{sinif}</td>
                                        <td className="whitespace-nowrap px-3 py-2 text-gray-800">{cinsiyet}</td>
                                        <td className="whitespace-nowrap px-3 py-2 text-gray-800">{kitapcik}</td>
                                        {canViewSensitiveInfo && <td className="whitespace-nowrap px-3 py-2 text-gray-800">{telNo}</td>}
                                        {isYks && (
                                            <td className="whitespace-nowrap px-3 py-2 text-right font-bold text-gray-900">
                                                {tytPuani != null && Number(tytPuani) > 0 ? Number(tytPuani).toFixed(2) : '–'}
                                            </td>
                                        )}
                                        <td className="whitespace-nowrap px-3 py-2 text-right">
                                            {isYks ? (
                                                yks?.alan && yks.score != null ? (
                                                    <>
                                                        <span className="font-bold text-gray-900">{yks.alan === 'SOZ' ? 'SÖZ' : yks.alan} {Number(yks.score).toFixed(2)}</span>
                                                        <span className="block text-[11px] text-gray-500">Alan sırası {rankLabel(yks.ranks?.general)}</span>
                                                    </>
                                                ) : (
                                                    <span className="text-gray-400" title="AYT'ye girmedi ya da deneme yalnız TYT">–</span>
                                                )
                                            ) : isLgs && lgs ? (
                                                <>
                                                    <span className="font-bold text-gray-900">{Number(lgs.totalNet).toFixed(2)} net</span>
                                                    <span className="block text-[11px] text-gray-500">
                                                        Genel {rankLabel(lgs.ranks?.general)} · {row?.student?.external ? 'Okul' : 'Kurum'} {rankLabel(lgs.ranks?.school)}
                                                    </span>
                                                </>
                                            ) : alan?.alan ? (
                                                <>
                                                    <span className="font-bold text-gray-900">{alan.alan === 'SOZ' ? 'SÖZ' : alan.alan} {Number(alan.score).toFixed(2)}</span>
                                                    <span className="block text-[11px] text-gray-500">
                                                        Alan sırası {rankLabel(alan.ranks?.general)}{row?.student?.external ? ` · Okul ${rankLabel(alan.ranks?.school)}` : ''}
                                                    </span>
                                                </>
                                            ) : (
                                                <span className={`font-bold ${scorePercentage >= 50 ? 'text-emerald-600' : 'text-red-600'}`}>
                                                    %{Number(scorePercentage).toFixed(2)}
                                                </span>
                                            )}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-center">
                                            <button
                                                onClick={() => onOpenDetail({ ...row, fullName })}
                                                className="rounded-full p-1.5 text-blue-600 transition-colors hover:bg-blue-50"
                                                title="Detaylı Sonuçlar">
                                                <Eye className="h-4 w-4" />
                                            </button>
                                            {canEditIdentity && withoutAccount && (
                                                <button
                                                    onClick={() => setIdentityRow(row)}
                                                    className={`rounded-full p-1.5 transition-colors hover:bg-amber-50 ${missing.length ? 'text-red-600' : 'text-gray-500'}`}
                                                    title="Kimliği düzelt">
                                                    <PenSquare className="h-4 w-4" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {pageCount > 1 && (
                <div className="flex items-center justify-end gap-2 text-sm">
                    <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1 || loading}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                        <ChevronLeft size={15} /> Önceki
                    </button>
                    <span className="text-gray-600">Sayfa {page} / {pageCount}</span>
                    <button
                        onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                        disabled={page >= pageCount || loading}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                        Sonraki <ChevronRight size={15} />
                    </button>
                </div>
            )}

            {identityRow && (
                // key: her satır için yeniden kurulur; form ilk çizimde o satırın bilgileriyle başlar (boş anda kaydedilip
                // dolu T.C.'yi silmesin)
                <IdentityEditModal
                    key={identityRow.id}
                    examId={exam.id}
                    row={identityRow}
                    canViewSensitiveInfo={canViewSensitiveInfo}
                    onClose={() => setIdentityRow(null)}
                    onSaved={() => { setIdentityRow(null); setReloadKey((k) => k + 1); }}
                />
            )}
        </div>
    );
}

interface IdentityEditModalProps {
    examId: string;
    row: any;
    canViewSensitiveInfo: boolean;
    onClose: () => void;
    onSaved: () => void;
}

/**
 * Optikte eksik ya da yanlış kodlanan kimlik. Kurum dışı öğrenci karnesine T.C. + okul numarasıyla ulaşır; ikisi de dolu
 * olmalı. Düzeltme sonraki optik yüklemesinde korunur (optikte boş kalan alanlar için).
 */
function IdentityEditModal({ examId, row, canViewSensitiveInfo, onClose, onSaved }: IdentityEditModalProps) {
    const meta = row?.student?.metadata || {};
    const initial = {
        fullName: meta.parsedName || row?.results?.studentInfo?.fullName || '',
        className: meta.className || '',
        studentNumber: meta.studentNumber || '',
        tcNumber: canViewSensitiveInfo ? (meta.tcNumber || '') : '',
    };
    const tcHidden = !canViewSensitiveInfo && Boolean(meta.tcNumber);

    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const set = (key: keyof typeof initial, value: string) => setForm((prev) => ({ ...prev, [key]: value }));
    const tcInvalid = form.tcNumber !== '' && !/^\d{11}$/.test(form.tcNumber);
    const numberInvalid = form.studentNumber !== '' && !/^\d{1,10}$/.test(form.studentNumber);

    const save = async () => {
        const changes: Record<string, string | null> = {};
        (['fullName', 'className', 'studentNumber'] as const).forEach((key) => {
            if (form[key].trim() !== initial[key]) changes[key] = form[key].trim() || null;
        });
        // Gizli T.C.: boş bırakılırsa değişmez; görünürse boşaltmak siler
        if (tcHidden ? form.tcNumber !== '' : form.tcNumber !== initial.tcNumber) changes.tcNumber = form.tcNumber || null;
        if (!Object.keys(changes).length) { onClose(); return; }
        setSaving(true);
        setError(null);
        try {
            await ApiService.updatePracticeExamResultIdentity(examId, row.id, changes);
            onSaved();
        } catch (err: any) {
            setError(err?.message || 'Kaydedilemedi');
        } finally {
            setSaving(false);
        }
    };

    const missingAfter = Boolean(row?.student?.external) && (!(form.tcNumber || tcHidden) || !form.studentNumber);

    return (
        <Modal isOpen onClose={() => !saving && onClose()} title="Kimliği düzelt" size="md">
            <div className="space-y-3">
                <p className="text-sm text-gray-600">
                    Optikte eksik ya da yanlış kodlanan bilgileri düzeltin. Kurum dışı öğrenci karnesine <b>T.C. kimlik + okul numarasıyla</b> ulaşır.
                    Düzeltme, aynı denemeye optik yeniden yüklendiğinde de korunur.
                </p>
                <label className="block text-sm">
                    <span className="mb-1 block font-medium text-gray-700">Ad soyad</span>
                    <input value={form.fullName} onChange={(e) => set('fullName', e.target.value)} maxLength={120} className="w-full rounded-md border border-gray-300 px-3 py-2" />
                </label>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="block text-sm">
                        <span className="mb-1 block font-medium text-gray-700">Okul numarası</span>
                        <input
                            value={form.studentNumber}
                            inputMode="numeric"
                            onChange={(e) => set('studentNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
                            className={`w-full rounded-md border px-3 py-2 ${numberInvalid ? 'border-red-400' : 'border-gray-300'}`}
                        />
                    </label>
                    <label className="block text-sm">
                        <span className="mb-1 block font-medium text-gray-700">Sınıf / şube</span>
                        <input value={form.className} onChange={(e) => set('className', e.target.value)} maxLength={20} placeholder="8/A" className="w-full rounded-md border border-gray-300 px-3 py-2" />
                    </label>
                </div>
                <label className="block text-sm">
                    <span className="mb-1 block font-medium text-gray-700">T.C. kimlik numarası</span>
                    <input
                        value={form.tcNumber}
                        inputMode="numeric"
                        autoComplete="off"
                        onChange={(e) => set('tcNumber', e.target.value.replace(/\D/g, '').slice(0, 11))}
                        placeholder={tcHidden ? 'Kayıtlı (gizli). Değiştirmek için yazın' : '11 haneli'}
                        className={`w-full rounded-md border px-3 py-2 tracking-wider ${tcInvalid ? 'border-red-400' : 'border-gray-300'}`}
                    />
                    {tcInvalid && <span className="mt-1 block text-xs text-red-600">T.C. kimlik numarası 11 haneli olmalı.</span>}
                </label>
                {missingAfter && (
                    <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">T.C. ve okul numarası dolmadan öğrenci karnesine ulaşamaz.</div>
                )}
                {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
                <div className="flex flex-col-reverse gap-2 border-t border-gray-200 pt-3 sm:flex-row sm:justify-end">
                    <button onClick={onClose} disabled={saving} className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 disabled:opacity-50">
                        İptal
                    </button>
                    <button
                        onClick={save}
                        disabled={saving || tcInvalid || numberInvalid}
                        className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                        {saving && <Loader2 size={16} className="animate-spin" />} Kaydet
                    </button>
                </div>
            </div>
        </Modal>
    );
}
